import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import SearchBar from '../components/molecules/SearchBar';
import ComponentCard from '../components/organisms/ComponentCard';
import {
  getAllComponents,
  deleteComponent,
} from "../api/componentService";
import { getUserRole } from '../utils/auth';

/**
 * ComponentManager Page
 * Displays available components with search and filtering capabilities
 */

function ComponentManager() {
  const navigate = useNavigate();
  // State management
  const [components, setComponents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = getUserRole() === 'ADMIN';

  // Load components on mount and after login
  useEffect(() => {
    loadComponents();
    // Listen for login event (token set)
    const onStorage = (e) => {
      if (e.key === 'jwt') {
        loadComponents();
      }
    };
    window.addEventListener('storage', onStorage);
    // Also reload when jwt changes in this tab
    const jwtListener = () => {
      loadComponents();
    };
    window.addEventListener('jwtChanged', jwtListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('jwtChanged', jwtListener);
    };
  }, []);

  // Fetch components from backend
  const loadComponents = async () => {
    try {
      setLoading(true);
      const response = await getAllComponents();
      console.log('Fetched components:', response.data);
      setComponents(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load components. Please try again later.');
      console.error('Error loading components:', err);
      if (err.response) {
        console.error('Error response:', err.response);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter components based on search query
  const filteredComponents = components.filter(component => 
    component.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    component.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Action Handlers
  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleEdit = (id) => {
    // Navigate to edit page
    navigate(`/components/edit/${id}`);
  };

  const handleDelete = async (id) => {
    try {
      await deleteComponent(id);
      await loadComponents();
    } catch (err) {
      console.error('Error deleting component:', err);
      throw err; // Propagate error to ComponentCard for error handling
    }
  };

  const handleView = (id) => {
    // Navigate to component details page
    navigate(`/components/view/${id}`);
  };

  const handleCreate = () => {
    // Navigate to create page
    navigate('/components/create');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* <Navbar /> */}
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Welcome to the Component Library
          </h1>
          <p className="text-gray-600 mb-6">
            Explore a wide range of reusable components for your projects.
          </p>

          {/* Search Bar and Create Button */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex-1 max-w-md">
              <SearchBar
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search components..."
              />
            </div>
            {isAdmin && (
              <button
                onClick={handleCreate}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create Component
              </button>
            )}
          </div>
        </div>

        {/* Components Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">{error}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredComponents.length > 0 ? (
              filteredComponents.map(component => (
                <ComponentCard 
                  key={component.id} 
                  component={{
                    ...component,
                    type: component.category || 'UI Element',
                    tags: component.tags || [],
                    updatedAt: component.updatedAt || Date.now()
                  }}
                  isPrivate={component.visibility === 'private'}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onView={handleView}
                />
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-gray-500">
                No components found matching your search.
              </div>
            )}
          </div>
        )}

      </main>
    </div>
  );
}

export default ComponentManager;
